import { useState, useRef, useCallback } from 'react';
import { parseLeadFile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Upload, FileText, Mail, Loader2, Sparkles, X, Check } from 'lucide-react';
import { toast } from 'sonner';

export function LeadAgent({ onLeadParsed }) {
  const [mode, setMode] = useState(null); // 'file' | 'email' | null
  const [emailText, setEmailText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload an image (JPEG/PNG/WEBP) or PDF');
      return;
    }
    setFileName(file.name);
    setMode('file');
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await parseLeadFile(formData);
      setPreview(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
      setPreview(null);
    } finally {
      setParsing(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleEmailParse = async () => {
    if (!emailText.trim()) { toast.error('Paste an email first'); return; }
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('email_text', emailText.trim());
      const result = await parseLeadFile(formData);
      setPreview(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse email');
      setPreview(null);
    } finally {
      setParsing(false);
    }
  };

  const confirmAdd = () => {
    if (preview && onLeadParsed) {
      onLeadParsed(preview);
    }
    reset();
  };

  const reset = () => {
    setMode(null);
    setEmailText('');
    setPreview(null);
    setFileName('');
    setDragOver(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  // If we have a preview, show the confirmation
  if (preview) {
    return (
      <Card className="border-dashed border-blue-200 bg-blue-50/30 p-3" data-testid="lead-agent-preview">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-3.5 w-3.5 text-blue-500" />
          <span className="text-[11px] font-semibold text-blue-700 uppercase tracking-wider">AI Extracted Lead</span>
        </div>
        <div className="space-y-1.5 text-[12px]">
          {preview.name && <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{preview.name}</span></div>}
          {preview.phone && <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{preview.phone}</span></div>}
          {preview.email && <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{preview.email}</span></div>}
          {preview.property_interest && <div><span className="text-muted-foreground">Interest:</span> <span className="font-medium">{preview.property_interest}</span></div>}
          {preview.budget && <div><span className="text-muted-foreground">Budget:</span> <span className="font-medium">{preview.budget}</span></div>}
          {(preview.desired_start_date || preview.desired_end_date) && (
            <div><span className="text-muted-foreground">Dates:</span> <span className="font-medium">{preview.desired_start_date || '?'} — {preview.desired_end_date || '?'}</span></div>
          )}
          {preview.notes && <div><span className="text-muted-foreground">Notes:</span> <span className="font-medium">{preview.notes}</span></div>}
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" className="h-7 text-xs gap-1" onClick={confirmAdd} data-testid="lead-agent-confirm">
            <Check className="h-3 w-3" /> Add to Sheet
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={reset} data-testid="lead-agent-cancel">
            <X className="h-3 w-3" /> Discard
          </Button>
        </div>
      </Card>
    );
  }

  // Parsing state
  if (parsing) {
    return (
      <Card className="border-dashed p-4 flex flex-col items-center justify-center" data-testid="lead-agent-parsing">
        <Loader2 className="h-5 w-5 animate-spin text-blue-500 mb-2" />
        <span className="text-[12px] text-muted-foreground">Analyzing {fileName || 'email'}...</span>
      </Card>
    );
  }

  // Text mode
  if (mode === 'email') {
    return (
      <Card className="border-dashed p-3" data-testid="lead-agent-email">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Paste Text</span>
          </div>
          <button onClick={reset} className="p-0.5 rounded hover:bg-muted"><X className="h-3 w-3 text-muted-foreground" /></button>
        </div>
        <Textarea
          value={emailText}
          onChange={e => setEmailText(e.target.value)}
          placeholder="Paste an email, message, or any text describing the lead..."
          className="text-[12px] min-h-[80px] resize-none"
          data-testid="lead-agent-email-input"
        />
        <Button size="sm" className="mt-2 h-7 text-xs gap-1 w-full" onClick={handleEmailParse} data-testid="lead-agent-email-parse">
          <Sparkles className="h-3 w-3" /> Extract Lead
        </Button>
      </Card>
    );
  }

  // Default: drop zone + email button
  return (
    <div className="space-y-2" data-testid="lead-agent">
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
        data-testid="lead-agent-file-input"
      />

      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-muted-foreground/20 hover:border-muted-foreground/40 hover:bg-muted/20'
        }`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        data-testid="lead-agent-dropzone"
      >
        <Upload className="h-5 w-5 mx-auto text-muted-foreground/50 mb-1.5" />
        <p className="text-[12px] text-muted-foreground">Drop image or PDF here</p>
        <p className="text-[10px] text-muted-foreground/60 mt-0.5">or click to browse</p>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="w-full h-7 text-xs gap-1.5"
        onClick={() => setMode('email')}
        data-testid="lead-agent-email-btn"
      >
        <Mail className="h-3 w-3" /> Paste Text or Email
      </Button>
    </div>
  );
}
